import { useState, useEffect, useCallback } from 'react';
import {
  IDecisionVoucherV2,
  DecisionVoucherV2Step,
} from '../models/decisionVoucherV2';
import { DecisionVoucherV2StorageService } from '../services/localStorage';

/**
 * Options pour le hook useDecisionVoucher
 */
interface UseDecisionVoucherOptions {
  autosave?: boolean;  // Sauvegarde automatique à chaque changement
  autosaveDelay?: number;  // Délai avant sauvegarde automatique (ms)
}

/**
 * Type de retour du hook useDecisionVoucher
 */
export interface UseDecisionVoucherReturn {
  // État du bon
  voucher: IDecisionVoucherV2 | null;
  isLoading: boolean;
  error: Error | null;
  isDirty: boolean;
  isSaving: boolean;
  saveError: Error | null;

  // Méthodes
  loadVoucher: (voucherId?: string) => Promise<IDecisionVoucherV2 | null>;
  saveVoucher: () => Promise<IDecisionVoucherV2 | null>;
  createNewDraft: () => Promise<IDecisionVoucherV2>;
  updateVoucher: (updates: Partial<IDecisionVoucherV2>) => void;

  // Navigation entre étapes
  goToNextStep: () => boolean;
  goToPreviousStep: () => boolean;
  setCurrentStep: (step: DecisionVoucherV2Step) => void;

  // Historique
  voucherHistory: IDecisionVoucherV2[];
  loadHistory: () => Promise<IDecisionVoucherV2[]>;
  switchToVersion: (voucherId: string) => Promise<IDecisionVoucherV2 | null>;
  createNewVersion: () => Promise<IDecisionVoucherV2 | null>;
}

/**
 * Hook personnalisé pour gérer un bon de décision
 */
export function useDecisionVoucher(
  savId: number,
  options: UseDecisionVoucherOptions = {}
): UseDecisionVoucherReturn {
  // Options par défaut
  const { autosave = true, autosaveDelay = 1000 } = options;

  // État
  const [voucher, setVoucher] = useState<IDecisionVoucherV2 | null>(null);
  const [_originalVoucher, setOriginalVoucher] = useState<IDecisionVoucherV2 | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [voucherHistory, setVoucherHistory] = useState<IDecisionVoucherV2[]>([]);

  // Timer pour autosave
  const [autosaveTimer, setAutosaveTimer] = useState<number | null>(null);

  /**
   * Charge un bon existant ou crée un nouveau brouillon
   */
  const loadVoucher = useCallback(async (voucherId?: string): Promise<IDecisionVoucherV2 | null> => {
    try {
      setIsLoading(true);
      setError(null);

      let loadedVoucher: IDecisionVoucherV2 | null = null;

      if (voucherId) {
        // Charger un bon spécifique
        loadedVoucher = DecisionVoucherV2StorageService.getVoucherById(voucherId);
      } else {
        // Charger le dernier brouillon ou en créer un nouveau
        loadedVoucher = DecisionVoucherV2StorageService.getLatestDraftForSav(savId);

        if (!loadedVoucher) {
          loadedVoucher = DecisionVoucherV2StorageService.createNewDraft(savId);
        }
      }

      if (loadedVoucher) {
        setVoucher(loadedVoucher);
        setOriginalVoucher(JSON.parse(JSON.stringify(loadedVoucher))); // Clone profond
        setIsDirty(false);
      }

      return loadedVoucher;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [savId]);

  /**
   * Sauvegarde le bon actuel
   */
  const saveVoucher = useCallback(async (): Promise<IDecisionVoucherV2 | null> => {
    if (!voucher) return null;

    try {
      setIsSaving(true);
      setSaveError(null);

      const savedVoucher = DecisionVoucherV2StorageService.saveVoucher(voucher);
      setVoucher(savedVoucher);
      setOriginalVoucher(JSON.parse(JSON.stringify(savedVoucher))); // Clone profond
      setIsDirty(false);

      return savedVoucher;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setSaveError(error);
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [voucher]);

  /**
   * Crée un nouveau brouillon
   */
  const createNewDraft = useCallback(async (): Promise<IDecisionVoucherV2> => {
    try {
      setIsLoading(true);
      setError(null);

      const newDraft = DecisionVoucherV2StorageService.createNewDraft(savId);
      setVoucher(newDraft);
      setOriginalVoucher(JSON.parse(JSON.stringify(newDraft))); // Clone profond
      setIsDirty(false);

      return newDraft;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [savId]);

  /**
   * Met à jour le bon avec de nouvelles valeurs
   */
  const updateVoucher = useCallback((updates: Partial<IDecisionVoucherV2>) => {
    setVoucher(current => {
      if (!current) return null;

      // Prévention des mises à jour redondantes
      let hasChanges = false;

      // Vérifier si les mises à jour contiennent réellement des changements
      for (const key in updates) {
        if (JSON.stringify(updates[key as keyof Partial<IDecisionVoucherV2>]) !==
            JSON.stringify(current[key as keyof IDecisionVoucherV2])) {
          hasChanges = true;
          break;
        }
      }

      // Pas de mise à jour si aucun changement
      if (!hasChanges) return current;

      const updated = {
        ...current,
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Marquer comme modifié uniquement si on a effectivement des changements
      setIsDirty(true);

      return updated;
    });
  }, []);

  /**
   * Passe à l'étape suivante du processus
   */
  const goToNextStep = useCallback((): boolean => {
    if (!voucher) return false;

    let nextStep: DecisionVoucherV2Step | null = null;

    switch (voucher.step) {
      case DecisionVoucherV2Step.QUALIFICATION:
        nextStep = DecisionVoucherV2Step.CONFIGURATION;
        break;
      case DecisionVoucherV2Step.CONFIGURATION:
        nextStep = DecisionVoucherV2Step.VALIDATION;
        break;
      case DecisionVoucherV2Step.VALIDATION:
        // Déjà à la dernière étape
        return false;
    }

    if (nextStep) {
      updateVoucher({ step: nextStep });
      return true;
    }

    return false;
  }, [voucher, updateVoucher]);

  /**
   * Revient à l'étape précédente du processus
   */
  const goToPreviousStep = useCallback((): boolean => {
    if (!voucher) return false;

    let previousStep: DecisionVoucherV2Step | null = null;

    switch (voucher.step) {
      case DecisionVoucherV2Step.QUALIFICATION:
        // Déjà à la première étape
        return false;
      case DecisionVoucherV2Step.CONFIGURATION:
        previousStep = DecisionVoucherV2Step.QUALIFICATION;
        break;
      case DecisionVoucherV2Step.VALIDATION:
        previousStep = DecisionVoucherV2Step.CONFIGURATION;
        break;
    }

    if (previousStep) {
      updateVoucher({ step: previousStep });
      return true;
    }

    return false;
  }, [voucher, updateVoucher]);

  /**
   * Définit directement l'étape courante
   */
  const setCurrentStep = useCallback((step: DecisionVoucherV2Step) => {
    updateVoucher({ step });
  }, [updateVoucher]);

  /**
   * Charge l'historique des bons pour ce SAV
   */
  const loadHistory = useCallback(async (): Promise<IDecisionVoucherV2[]> => {
    const history = DecisionVoucherV2StorageService.getAllVouchersForSav(savId);
    setVoucherHistory(history);
    return history;
  }, [savId]);

  /**
   * Passe à une version spécifique du bon
   */
  const switchToVersion = useCallback(async (voucherId: string): Promise<IDecisionVoucherV2 | null> => {
    return loadVoucher(voucherId);
  }, [loadVoucher]);

  /**
   * Crée une nouvelle version basée sur le bon actuel
   */
  const createNewVersion = useCallback(async (): Promise<IDecisionVoucherV2 | null> => {
    if (!voucher || !voucher.id) return null;

    try {
      const newVersion = DecisionVoucherV2StorageService.createVersionFromVoucher(voucher.id);

      if (newVersion) {
        setVoucher(newVersion);
        setOriginalVoucher(JSON.parse(JSON.stringify(newVersion))); // Clone profond
        setIsDirty(false);
      }

      return newVersion;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
      return null;
    }
  }, [voucher]);

  // Chargement initial
  useEffect(() => {
    // Utilisation d'une fonction d'effet interne pour éviter les dépendances circulaires
    const initialize = async () => {
      await loadVoucher();
      await loadHistory();
    };

    initialize();
    // Aucune dépendance - ce hook est exécuté une seule fois lors du montage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autosave lorsque isDirty est true
  useEffect(() => {
    if (!autosave || !isDirty || !voucher) return;

    // Annuler le timer précédent s'il existe
    if (autosaveTimer) {
      window.clearTimeout(autosaveTimer);
    }

    // Créer un nouveau timer
    const timerId = window.setTimeout(() => {
      // Fonction de sauvegarde directe pour éviter les dépendances circulaires
      const saveCurrentVoucher = async () => {
        if (!voucher) return null;

        try {
          setIsSaving(true);
          setSaveError(null);

          const savedVoucher = DecisionVoucherV2StorageService.saveVoucher(voucher);
          setVoucher(savedVoucher);
          setOriginalVoucher(JSON.parse(JSON.stringify(savedVoucher))); // Clone profond
          setIsDirty(false);
        } catch (err) {
          const error = err instanceof Error ? err : new Error(String(err));
          setSaveError(error);
        } finally {
          setIsSaving(false);
        }
      };

      saveCurrentVoucher();
    }, autosaveDelay);

    setAutosaveTimer(timerId);

    // Cleanup
    return () => {
      if (timerId) window.clearTimeout(timerId);
    };
    // N'incluez pas saveVoucher ou autosaveTimer dans les dépendances
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, autosave, autosaveDelay, voucher]);

  return {
    voucher,
    isLoading,
    error,
    isDirty,
    isSaving,
    saveError,
    loadVoucher,
    saveVoucher,
    createNewDraft,
    updateVoucher,
    goToNextStep,
    goToPreviousStep,
    setCurrentStep,
    voucherHistory,
    loadHistory,
    switchToVersion,
    createNewVersion,
  };
}